import { useMemo, useState, useEffect } from 'react';
import { debounce } from 'lodash';
import { useFetchingContext, useFiltersContext, useProductsContext, useWithoutSearchingContext } from 'context';

const useTableData = () => {
    const { setWithoutSearchings } = useWithoutSearchingContext();
    const { products } = useProductsContext();
    const { loading } = useFetchingContext();
    const { searchTerms, filters } = useFiltersContext();

    const [debouncedSearchTerms, setDebouncedSearchTerms] = useState(searchTerms);

    useEffect(() => {
        const handler = debounce(() => {
            setDebouncedSearchTerms(searchTerms);
        }, 150); // Espera 150ms después de que el usuario deje de escribir

        handler();
        return () => handler.cancel();
    }, [searchTerms]);

    const filteredByTerms = useMemo(() => {
        if (!debouncedSearchTerms.trim()) return products;

        const filterText = debouncedSearchTerms.toLowerCase();
        const result = products.filter((group) => {
            return (
                (group?.barcode && group?.barcode.includes(debouncedSearchTerms)) ||
                (group?.description && group?.description.toLowerCase().includes(filterText)) ||
                (group?.laboratory && group?.laboratory.toLowerCase().includes(filterText)) ||
                (group?.drug && group?.drug.toLowerCase().includes(filterText))
            );
        });

        setWithoutSearchings(result.length === 0);
        return result;
    }, [debouncedSearchTerms, products]);

    const filteredByFilters = useMemo(() => {
        if (filters.length === 0 || filters.every((filter) => !filter.isChecked)) {
            return filteredByTerms; // Si no hay filtros, devolvemos lo filtrado por términos de búsqueda
        }

        const isDiscountChecked = filters.some((filter) => filter.name === 'Con descuento' && filter.isChecked);
        const isOutOfStockChecked = filters.some((filter) => filter.name === 'Sin stock' && filter.isChecked);
        const isGenericChecked = filters.some((filter) => filter.name === 'Con genéricos' && filter.isChecked);

        const result = filteredByTerms.filter((product) => {
            let matches = true;
            if (isDiscountChecked) {
                matches =
                    matches &&
                    Object.values(product.products).some(
                        (p: any) => p?.agreement || (p?.discounts && p?.discounts.length > 0),
                    );
            }
            if (isOutOfStockChecked) {
                matches = matches && !product.is_product_in_stock;
            }
            if (isGenericChecked) {
                matches = matches && product?.generics?.length > 0;
            }
            return matches;
        });

        setWithoutSearchings(result.length === 0);
        return result;
    }, [filteredByTerms, filters]);

    const data = useMemo(() => {
        if (loading) return Array(6).fill(0);

        return filteredByFilters;
    }, [loading, filteredByFilters]);

    return { data };
};

export default useTableData;
